import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Paper,
  useTheme,
  alpha,
  Typography,
  Button,
  Link as MuiLink,
  Card,
  CardContent,
  Stepper,
  Step,
  Container,
  StepLabel,
  StepConnector,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  ListAlt as ListAltIcon,
  BugReport as BugReportIcon,
  MonetizationOn as MonetizationOnIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  LocalMall as LocalMallIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import OverviewTab from './OverviewTab';
import IngredientsTab from './IngredientsTab';
import ProcedureTab from './ProcedureTab';
import TestsTab from './TestsTab';
import CostsTab from './CostsTab';
import SummaryTab from './SummaryTab';

// Styled components matching invoice page patterns
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Custom Timeline Connector
const TimelineConnector = styled(StepConnector)(({ theme, active, completed }) => ({
  '& .MuiStepConnector-line': {
    height: 4,
    border: 0,
    backgroundColor: completed ? '#4caf50' : active ? '#ff9800' : '#e0e0e0',
    borderRadius: 2,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::after': completed ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
      animation: 'shimmer 2s infinite'
    } : {}
  },
  '@keyframes shimmer': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' }
  }
}));

// Custom Timeline Step Icon
const TimelineStepIcon = styled('div')(({ theme, active, completed, stepIndex }) => {
  const colors = [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main, theme.palette.error.main, '#9c27b0'];
  const bgColor = completed ? theme.palette.success.main : active ? colors[stepIndex] : '#e0e0e0';
  
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: bgColor,
    border: active ? `3px solid ${alpha(colors[stepIndex] || '#1976d2', 0.3)}` : completed ? `3px solid ${theme.palette.success.main}` : '3px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: completed || active ? 'white' : '#999',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    boxShadow: active ? `0 0 20px ${alpha(colors[stepIndex] || '#1976d2', 0.4)}` : completed ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 6px 20px ${alpha(bgColor || '#e0e0e0', 0.4)}`
    },
    '& .MuiSvgIcon-root': {
      fontSize: 28
    }
  };
});

// Timeline Component
function FormulationTimeline({ activeStep, onStepClick, formulation, formulationIngredients, availableTabs }) {
  const theme = useTheme();
  const { canAccessPage } = usePermissions();
  
  // Dynamic completion check functions
  const checkSetupCompleted = (formulation) => {
    if (!formulation) return false;
    
    // ALL basic information fields must be filled
    const hasBasicInfo = !!(
      formulation.name?.trim() && 
      formulation.code?.trim() &&
      formulation.product_id &&
      formulation.target_batch_size && 
      formulation.unit_of_measure?.trim() &&
      formulation.version?.trim() &&
      formulation.description?.trim()
    );
    
    // ALL additional information fields must be filled
    const hasAdditionalInfo = !!(
      formulation.status?.trim() &&
      formulation.type?.trim() &&
      formulation.notes?.trim()
    );
    
    // ALL technical specifications must be filled
    const hasTechnicalSpecs = !!(
      formulation.ph_target && 
      formulation.viscosity_target?.trim() && 
      formulation.appearance?.trim() &&
      formulation.yield_percentage
    );
    
    return hasBasicInfo && hasAdditionalInfo && hasTechnicalSpecs;
  };
  
  // Check completion status for each step from database and current data
  const setupCompleted = checkSetupCompleted(formulation);
  const formulateCompleted = formulation?.ingredients_complete || false;
  const documentCompleted = formulation?.procedure_complete || false;
  const validateCompleted = formulation?.tests_complete || false;
  const analyzeCompleted = formulation?.costs_complete || false;
  const reviewCompleted = formulation?.status === 'approved';

  const allTimelineSteps = [
    {
      label: 'Setup',
      sublabel: 'Basic Information',
      icon: <AssignmentIcon />,
      description: 'Define formulation details and specifications',
      completed: setupCompleted,
      permission: 'formulations_overview'
    },
    {
      label: 'Formulate',
      sublabel: 'Ingredient Composition',
      icon: <ScienceIcon />,
      description: 'Add and configure ingredient percentages',
      completed: formulateCompleted,
      permission: 'formulations_ingredients'
    },
    {
      label: 'Document',
      sublabel: 'Mixing Procedure',
      icon: <ListAltIcon />,
      description: 'Create step-by-step mixing instructions',
      completed: documentCompleted,
      permission: 'formulations_procedures'
    },
    {
      label: 'Validate',
      sublabel: 'Quality Testing',
      icon: <BugReportIcon />,
      description: 'Perform stability and quality tests',
      completed: validateCompleted,
      permission: 'formulations_tests'
    },
    {
      label: 'Analyze',
      sublabel: 'Cost Calculation',
      icon: <MonetizationOnIcon />,
      description: 'Review formulation costs and margins',
      completed: analyzeCompleted,
      permission: 'formulations_costs'
    },
    {
      label: 'Review',
      sublabel: 'Final Summary',
      icon: <CheckCircleIcon />,
      description: 'Review complete formulation and approve',
      completed: reviewCompleted,
      permission: 'formulations_summary'
    }
  ];

  // Filter timeline steps based on available tabs
  const timelineSteps = allTimelineSteps.filter(step => {
    return availableTabs.some(tab => tab.permission === step.permission);
  });

  return (
    <Box sx={{ 
      p: 3,
      borderRadius: 4,
      mb: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
            Formulation Development Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Track your progress through the formulation development process
          </Typography>
        </Box>
        <Box sx={{ 
          px: 3, 
          py: 1, 
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Step {activeStep + 1} of {timelineSteps.length}
          </Typography>
        </Box>
      </Box>

              <Paper sx={{ 
          p: 3, 
          borderRadius: 3, 
          mb: 3, 
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.9)}`
      }}>
        <Stepper 
          activeStep={activeStep} 
          connector={<TimelineConnector />}
          sx={{ 
            '& .MuiStepLabel-root': {
              cursor: 'pointer'
            }
          }}
        >
          {timelineSteps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = step.completed;
            
            return (
              <Step key={step.label} onClick={() => onStepClick(index)}>
                <StepLabel
                  StepIconComponent={() => (
                    <TimelineStepIcon 
                      active={isActive} 
                      completed={isCompleted}
                      stepIndex={index}
                    >
                      {isCompleted ? <CheckCircleIcon /> : step.icon}
                    </TimelineStepIcon>
                  )}
                  sx={{
                    '& .MuiStepLabel-labelContainer': {
                      mt: 2
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        mb: 0.5,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        maxWidth: 140,
                        lineHeight: 1.3
                      }}
                    >
                      {step.description}
                    </Typography>
                    
                    {/* Status Indicator */}
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isCompleted ? (
                          '✓ Complete'
                        ) : (
                          <>
                            {isActive && <ScheduleIcon sx={{ fontSize: 12 }} />}
                            {isActive ? 'In Progress' : 'Pending'}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Overall Progress
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
            {Math.round((timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100)}%
          </Typography>
        </Box>
        <Box sx={{ 
          width: '100%', 
          height: 8, 
          backgroundColor: alpha(theme.palette.grey[400], 0.3),
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{
            width: `${(timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: 20,
              height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 100%)',
              animation: 'progressShine 2s infinite'
            }
          }} />
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => onStepClick(Math.max(0, activeStep - 1))}
          disabled={activeStep === 0}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            borderColor: 'primary.main',
            color: 'primary.main',
            fontWeight: 500,
            background: 'white',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'white',
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            '&:disabled': {
              borderColor: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled',
              backgroundColor: 'white'
            },
            transition: 'all 0.2s ease'
          }}
        >
          ← Previous Step
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {timelineSteps.map((_, index) => (
            <Box
              key={index}
              onClick={() => onStepClick(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === activeStep ? 'primary.main' : index < activeStep ? 'success.main' : alpha(theme.palette.grey[400], 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.3)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={() => onStepClick(Math.min(timelineSteps.length - 1, activeStep + 1))}
          disabled={activeStep === timelineSteps.length - 1 || (activeStep === 0 && formulation && (!formulation.name?.trim() || !formulation.description?.trim() || !formulation.type?.trim() || !formulation.product_id))}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
            fontWeight: 500,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              transform: 'translateY(-1px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
            },
            '&:disabled': {
              background: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Next Step →
        </Button>
      </Box>
    </Box>
  );
}

const FormulationTabs = forwardRef(({ 
  formulation, 
  rawMaterials,
  phases,
  showSnackbar,
  user,
  products,
  formulationIngredients,
  userCompanyId,
  onUpdate,
  onBackToList
}, ref) => {
  const { companyId } = useParams();
  const theme = useTheme();
  const { canAccessPage, canEditPage } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openEditDialog: (isCreatingNew = false) => {
      // Find the OverviewTab ref and call its openEditDialog method
      if (activeTab === 0) {
        // If we're on overview tab, call the edit dialog directly
        // This will be handled by the OverviewTab component
      } else {
        // Switch to overview tab first, then open edit dialog
        setActiveTab(0);
        setTimeout(() => {
          // The OverviewTab should handle this
        }, 100);
      }
    }
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTimelineStepClick = (stepIndex) => {
    // Block navigation only for NEW formulations if user doesn't have overview permissions
    const isCreatingNew = !formulation?.id;
    if (isCreatingNew && stepIndex > 0 && (!canAccessPage('formulations_overview') || !canEditPage('formulations_overview'))) {
      return;
    }
    
    // Map the timeline step index to the actual tab index in filtered tabsData
    if (stepIndex >= 0 && stepIndex < tabsData.length) {
      setActiveTab(stepIndex);
    }
  };

  const allTabsData = [
    {
      label: 'Overview',
      icon: <AssignmentIcon />,
      permission: 'formulations_overview',
      component: (
        <OverviewTab 
          formulation={formulation}
          products={products}
          showSnackbar={showSnackbar}
          isCreatingNew={!formulation?.id}
          userCompanyId={userCompanyId}
          formulationIngredients={formulationIngredients}
          onUpdate={onUpdate}
          user={user}
          onBackToList={onBackToList}
        />
      )
    },
    {
      label: 'Ingredients',
      icon: <ScienceIcon />,
      permission: 'formulations_ingredients',
      component: (
        <IngredientsTab 
          formulation={formulation}
          rawMaterials={rawMaterials}
          phases={phases}
          formulationIngredients={formulationIngredients}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          user={user}
          onUpdate={onUpdate}
        />
      )
    },
    {
      label: 'Procedure',
      icon: <ListAltIcon />,
      permission: 'formulations_procedures',
      component: (
        <ProcedureTab 
          formulation={formulation}
          phases={phases}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          user={user}
          onUpdate={onUpdate}
        />
      )
    },
    {
      label: 'Tests',
      icon: <BugReportIcon />,
      permission: 'formulations_tests',
      component: (
        <TestsTab 
          formulation={formulation}
          products={products}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          user={user}
          onUpdate={onUpdate}
        />
      )
    },
    {
      label: 'Costs',
      icon: <MonetizationOnIcon />,
      permission: 'formulations_costs',
      component: (
        <CostsTab 
          formulation={formulation}
          formulationIngredients={formulationIngredients}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          user={user}
          onUpdate={onUpdate}
        />
      )
    },
    {
      label: 'Summary',
      icon: <CheckCircleIcon />,
      permission: 'formulations_summary',
      component: (
        <SummaryTab 
          formulation={formulation}
          formulationIngredients={formulationIngredients}
          phases={phases}
          showSnackbar={showSnackbar}
          user={user}
          onUpdate={onUpdate}
        />
      )
    }
  ];

  // Filter tabs based on permissions
  const tabsData = allTabsData.filter(tab => {
    return canAccessPage(tab.permission);
  });

  // Auto-select first available tab if current tab is not accessible
  useEffect(() => {
    if (tabsData.length > 0 && activeTab === 0) {
      const currentTab = tabsData[0];
      if (currentTab) {
        const hasAccess = canAccessPage(currentTab.permission);
        if (!hasAccess && tabsData.length > 1) {
          const firstAccessibleIndex = tabsData.findIndex(tab => 
            canAccessPage(tab.permission)
          );
          if (firstAccessibleIndex >= 0) {
            setActiveTab(firstAccessibleIndex);
          }
        }
      }
    }
  }, [tabsData.length]);

  // If no tabs are available, show access restricted message
  if (tabsData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation View Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have permissions to access any formulation sections. Contact your system administrator to request access to overview, ingredients, procedures, tests, costs, or summary.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Page Header */}
              <Card sx={{ 
          borderRadius: 3,
          mb: 3,
          mt: 5,
        border: `1px solid ${theme.palette.grey[400]}`,
        background: theme.palette.grey[100],
        backdropFilter: 'blur(20px)',
        borderTop: '3px solid #E91E63',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#E91E63', 0.08),
              }}>
                <ScienceIcon sx={{ fontSize: 32, color: '#E91E63' }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#E91E63',
                  mb: 0.5,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  {formulation?.name || 'New Formulation'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  {formulation?.description || 'Formulation Development Lab'}
                </Typography>
              </Box>
          </Box>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={onBackToList}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: 'grey.400',
              color: 'primary.main',
              borderWidth: 1,
              fontWeight: 500,
              px: 3,
              py: 1.5,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'white',
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
              }
            }}
          >
            Back to Formulations
          </Button>
        </Box>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            maxWidth: 600, 
            lineHeight: 1.5,
            fontSize: '0.95rem',
            fontWeight: 500
          }}>
            Create and manage product formulations with precise ingredient tracking and cost analysis.
          </Typography>
        </CardContent>
      </Card>

      {/* Timeline Component */}
      <FormulationTimeline 
        activeStep={activeTab} 
        onStepClick={handleTimelineStepClick}
        formulation={formulation}
        formulationIngredients={formulationIngredients}
        availableTabs={tabsData}
      />

      {/* Main Content Area */}
      <SectionPaper>
        {tabsData[activeTab]?.component}
      </SectionPaper>

      <style>
        {`
          @keyframes progressShine {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
});

FormulationTabs.displayName = 'FormulationTabs';

export default FormulationTabs;